/**
 * COD report aggregation — pure functions over data the portal already loads
 * (GET /orders/cod/*, GET /cod/invoices, GET /cod, GET /customers/search). No API calls here.
 *
 * COD money is tracked per Sage invoice: when an order's invoices are known (invoice index),
 * invoiced / collected / remaining come from its invoices. Orders without invoice data fall back
 * to the order-level amounts so the report still works if the invoice call fails.
 */
import { CodInvoice, CodOrder, CodOrderInvoices, CodPaymentTransaction } from "../types/codSalesRep";
import {
  computeItemTotal,
  getOrderOutstanding,
  getOrderPayableTotal,
  isRejectedCodOrder,
} from "./codUtils";

export interface ReportDateRange {
  /** yyyy-mm-dd, inclusive. Empty = no lower bound. */
  from: string;
  /** yyyy-mm-dd, inclusive. Empty = no upper bound. */
  to: string;
}

export interface ReportCustomerRef {
  customerId: string;
  customerCode: string;
  customerName: string;
}

export interface ReportTotals {
  orders: number;
  pending: number;
  approved: number;
  rejected: number;
  approvedValue: number;
  /** Sum of the Sage invoice amounts issued for approved orders. */
  invoiced: number;
  /** Paid on those invoices (in the app or directly in Sage). */
  collected: number;
  /** Still owed on issued invoices. */
  remaining: number;
  /** Approved order lines Sage has not invoiced yet (line value). */
  notInvoiced: number;
  /** Number of Sage invoices. */
  invoiceCount: number;
  pendingValue: number;
  payments: number;
  lastOrder?: string;
  lastPayment?: string;
}

export interface CustomerReportRow extends ReportCustomerRef, ReportTotals {
  key: string;
  orderList: CodOrder[];
  paymentList: CodPaymentTransaction[];
}

export interface RepReportRow extends ReportTotals {
  key: string;
  repName: string;
  repCode: string;
  customers: CustomerReportRow[];
  orderList: CodOrder[];
  paymentList: CodPaymentTransaction[];
}

export type OrderBucket = "pending" | "approved" | "rejected";

export const orderBucket = (order: CodOrder): OrderBucket => {
  if (isRejectedCodOrder(order)) return "rejected";
  if (order.approvalStatus === 2 || order.approvalStatusName === "Approved") return "approved";
  return "pending";
};

/** Money actually collected on an order (never more than it owes). */
export const getOrderCollected = (order: CodOrder): number => {
  const payable = getOrderPayableTotal(order);
  const paid = Number(order.amountPaid ?? 0);
  if (Number.isFinite(paid) && paid > 0) return Math.min(paid, payable || paid);
  return Math.max(0, payable - getOrderOutstanding(order));
};

/** orderId → its lines and Sage invoices (from GET /cod/invoices?includePaid=true). */
export type InvoiceIndex = Map<string, CodOrderInvoices>;

export const buildInvoiceIndex = (orders: CodOrderInvoices[]): InvoiceIndex =>
  new Map(orders.map((order) => [order.orderId, order]));

export interface OrderFinancials {
  invoiced: number;
  collected: number;
  remaining: number;
  notInvoiced: number;
  invoiceCount: number;
  /** True when the figures come from Sage invoices. */
  fromInvoices: boolean;
}

/** Money figures for one approved order — from its Sage invoices when known. */
export const orderFinancials = (order: CodOrder, index?: InvoiceIndex): OrderFinancials => {
  const entry = index?.get(order.id);
  if (entry) {
    const invoices: CodInvoice[] = entry.lines.flatMap((line) => line.invoices);
    return {
      invoiced: invoices.reduce((sum, invoice) => sum + invoice.amount, 0),
      collected: invoices.reduce((sum, invoice) => sum + invoice.paidAmount, 0),
      remaining: invoices.reduce((sum, invoice) => sum + invoice.remainingAmount, 0),
      notInvoiced: entry.lines
        .filter((line) => !line.hasInvoice || line.invoices.length === 0)
        .reduce((sum, line) => sum + line.lineTotal, 0),
      invoiceCount: invoices.length,
      fromInvoices: true,
    };
  }
  // Fallback: order-level amounts (no invoice data for this order).
  return {
    invoiced: 0,
    collected: getOrderCollected(order),
    remaining: getOrderOutstanding(order),
    notInvoiced: 0,
    invoiceCount: 0,
    fromInvoices: false,
  };
};

const dayStart = (value: string) => new Date(`${value}T00:00:00`).getTime();
const dayEnd = (value: string) => new Date(`${value}T23:59:59.999`).getTime();

export const inDateRange = (value: string | undefined, range: ReportDateRange): boolean => {
  if (!range.from && !range.to) return true;
  if (!value) return false;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return false;
  if (range.from && time < dayStart(range.from)) return false;
  if (range.to && time > dayEnd(range.to)) return false;
  return true;
};

export const orderDate = (order: CodOrder) => order.date || order.submittedAt;

const customerKeyOf = (ref: { customerId?: string; customerCode?: string }) =>
  ref.customerId?.trim() || (ref.customerCode?.trim() ? `code:${ref.customerCode.trim().toLowerCase()}` : "");

const latest = (a?: string, b?: string) => {
  if (!a) return b;
  if (!b) return a;
  return new Date(a).getTime() >= new Date(b).getTime() ? a : b;
};

const emptyTotals = (): ReportTotals => ({
  orders: 0,
  pending: 0,
  approved: 0,
  rejected: 0,
  approvedValue: 0,
  invoiced: 0,
  collected: 0,
  remaining: 0,
  notInvoiced: 0,
  invoiceCount: 0,
  pendingValue: 0,
  payments: 0,
});

const isCountablePayment = (payment: CodPaymentTransaction) =>
  !/fail|cancel|reject|void/i.test(payment.status ?? "");

const addOrder = (totals: ReportTotals, order: CodOrder, index?: InvoiceIndex) => {
  totals.orders += 1;
  const bucket = orderBucket(order);
  if (bucket === "pending") {
    totals.pending += 1;
    totals.pendingValue += getOrderPayableTotal(order);
  } else if (bucket === "approved") {
    totals.approved += 1;
    totals.approvedValue += getOrderPayableTotal(order);
    const money = orderFinancials(order, index);
    totals.invoiced += money.invoiced;
    totals.collected += money.collected;
    totals.remaining += money.remaining;
    totals.notInvoiced += money.notInvoiced;
    totals.invoiceCount += money.invoiceCount;
  } else {
    totals.rejected += 1;
  }
  totals.lastOrder = latest(totals.lastOrder, orderDate(order));
};

const addPayment = (totals: ReportTotals, payment: CodPaymentTransaction) => {
  if (!isCountablePayment(payment)) return;
  totals.payments += 1;
  totals.lastPayment = latest(totals.lastPayment, payment.paymentDate);
};

/** Keep only orders in range, and payments that belong to the given orders and are in range. */
export const scopeReportData = (
  orders: CodOrder[],
  payments: CodPaymentTransaction[],
  range: ReportDateRange
) => {
  const live = orders.filter((order) => !order.isDeleted);
  const allIds = new Set(live.map((order) => order.id));
  return {
    orders: live.filter((order) => inDateRange(orderDate(order), range)),
    // Payments are scoped to this user's orders (GET /cod is not filtered server-side).
    payments: payments.filter(
      (payment) => allIds.has(payment.orderId) && inDateRange(payment.paymentDate, range)
    ),
  };
};

/**
 * One row per customer. `knownCustomers` lets customers with no orders in range still appear
 * (e.g. every customer assigned to the rep).
 */
export const buildCustomerRows = (
  orders: CodOrder[],
  payments: CodPaymentTransaction[],
  allOrders: CodOrder[],
  knownCustomers: ReportCustomerRef[] = [],
  invoiceIndex?: InvoiceIndex
): CustomerReportRow[] => {
  const rows = new Map<string, CustomerReportRow>();
  const ensure = (ref: Partial<ReportCustomerRef>): CustomerReportRow | null => {
    const key = customerKeyOf(ref);
    if (!key) return null;
    let row = rows.get(key);
    if (!row) {
      row = {
        key,
        customerId: ref.customerId ?? "",
        customerCode: ref.customerCode ?? "",
        customerName: ref.customerName ?? "",
        ...emptyTotals(),
        orderList: [],
        paymentList: [],
      };
      rows.set(key, row);
    } else {
      row.customerId ||= ref.customerId ?? "";
      row.customerCode ||= ref.customerCode ?? "";
      row.customerName ||= ref.customerName ?? "";
    }
    return row;
  };

  knownCustomers.forEach((customer) => ensure(customer));

  // Customers can be known by id in one source and by code in another: map code → id row.
  const byCode = new Map<string, CustomerReportRow>();
  rows.forEach((row) => {
    if (row.customerCode) byCode.set(row.customerCode.trim().toLowerCase(), row);
  });
  const resolve = (ref: Partial<ReportCustomerRef>) => {
    const code = ref.customerCode?.trim().toLowerCase();
    if (ref.customerId && rows.has(ref.customerId)) return ensure(ref);
    if (code && byCode.has(code)) return byCode.get(code)!;
    const row = ensure(ref);
    if (row && code) byCode.set(code, row);
    return row;
  };

  orders.forEach((order) => {
    const row = resolve(order);
    if (!row) return;
    row.orderList.push(order);
    addOrder(row, order, invoiceIndex);
  });

  const orderById = new Map(allOrders.map((order) => [order.id, order]));
  payments.forEach((payment) => {
    const order = orderById.get(payment.orderId);
    const row = resolve({
      customerId: payment.customerId || order?.customerId,
      customerCode: payment.customerCode || order?.customerCode,
      customerName: order?.customerName,
    });
    if (!row) return;
    row.paymentList.push(payment);
    addPayment(row, payment);
  });

  return Array.from(rows.values()).sort(
    (a, b) => b.remaining - a.remaining || b.approvedValue - a.approvedValue ||
      (a.customerName || a.customerCode).localeCompare(b.customerName || b.customerCode)
  );
};

export interface RepRef {
  repName: string;
  repCode: string;
}

/** One row per sales rep, each with its customers. Reps with no orders still appear. */
export const buildRepRows = (
  orders: CodOrder[],
  payments: CodPaymentTransaction[],
  allOrders: CodOrder[],
  knownReps: RepRef[],
  unassignedLabel: string,
  invoiceIndex?: InvoiceIndex
): RepReportRow[] => {
  const reps = new Map<string, RepReportRow>();
  const ensure = (name: string, code = "") => {
    const repName = name.trim() || unassignedLabel;
    const key = repName.toLowerCase();
    let row = reps.get(key);
    if (!row) {
      row = { key, repName, repCode: code, ...emptyTotals(), customers: [], orderList: [], paymentList: [] };
      reps.set(key, row);
    } else if (!row.repCode && code) {
      row.repCode = code;
    }
    return row;
  };

  knownReps.forEach((rep) => ensure(rep.repName, rep.repCode));

  orders.forEach((order) => {
    const row = ensure(order.salesRepName ?? "");
    row.orderList.push(order);
    addOrder(row, order, invoiceIndex);
  });

  const orderById = new Map(allOrders.map((order) => [order.id, order]));
  payments.forEach((payment) => {
    const order = orderById.get(payment.orderId);
    const row = ensure(order?.salesRepName ?? "");
    row.paymentList.push(payment);
    addPayment(row, payment);
  });

  reps.forEach((row) => {
    row.customers = buildCustomerRows(row.orderList, row.paymentList, allOrders, [], invoiceIndex);
  });

  return Array.from(reps.values()).sort(
    (a, b) => b.approvedValue - a.approvedValue || b.orders - a.orders || a.repName.localeCompare(b.repName)
  );
};

export const sumTotals = (rows: ReportTotals[]): ReportTotals =>
  rows.reduce((acc, row) => {
    acc.orders += row.orders;
    acc.pending += row.pending;
    acc.approved += row.approved;
    acc.rejected += row.rejected;
    acc.approvedValue += row.approvedValue;
    acc.invoiced += row.invoiced;
    acc.collected += row.collected;
    acc.remaining += row.remaining;
    acc.notInvoiced += row.notInvoiced;
    acc.invoiceCount += row.invoiceCount;
    acc.pendingValue += row.pendingValue;
    acc.payments += row.payments;
    acc.lastOrder = latest(acc.lastOrder, row.lastOrder);
    acc.lastPayment = latest(acc.lastPayment, row.lastPayment);
    return acc;
  }, emptyTotals());

/**
 * Share of what is billable that has been collected. With invoice data this is collected ÷
 * invoiced (only invoiced amounts can be paid); otherwise collected ÷ approved value.
 */
export const collectionRate = (
  totals: Pick<ReportTotals, "approvedValue" | "collected"> & Partial<Pick<ReportTotals, "invoiced">>
) => {
  const base = (totals.invoiced ?? 0) > 0 ? (totals.invoiced as number) : totals.approvedValue;
  return base > 0 ? Math.min(1, totals.collected / base) : 0;
};

export const lineTotal = computeItemTotal;
